import { Box, Typography } from "@mui/material";
import useColors from "../../../../../../../api/hooks/useColors";
import LetterAvatar from "../../../../../../../components/assignee/LetterAvatar";
import CustomTooltip from "../../../../../../../components/CustomTooltip";
import SvgToIcon from "../../../../../../../icons/SvgToIcon";
import IntegrationImage from "../../../../../../../scenes/stack/integrations/components/IntegrationImage";
import {
  ActivityLogType,
  BREEZE_LOG_USER_ID,
} from "../../../../../../../utils/constants";
import {
  getPrettyDateTime,
  timePassedSince,
  useLocalTime,
} from "../../../../../../../utils/timestamp";
import { CustomLogTypography } from "./contentCreators/utils";

import { useLogContentCreator } from "./contentCreators/LogContentCreator";
import { logIconsMapping } from "./LogIconsMapping";

export const VerticalBottomLine = ({ colors }) => (
  <Box
    position="absolute"
    width="1px"
    height="12px"
    bottom="-6px"
    left="11px"
    border={`1px solid ${colors.divider}`}
  ></Box>
);

export default function Log({ log, includeBottomVerticalLine = true }) {
  const colors = useColors();

  const logContent = useLogContentCreator(log);
  const userDisplayName = (log.user_display_name || "").split(" ")[0];
  const showTimeAtLocalTime = useLocalTime();

  const showUserAvatar = [
    ActivityLogType.ASSIGNED,
    ActivityLogType.UNASSIGNED,
  ].includes(log.log_type);

  const showTicketingIntegrationsIcon = [
    ActivityLogType.TICKET_CREATED,
    ActivityLogType.TICKET_LINKED,
    ActivityLogType.TICKET_CREATED_FOR_ENTITY,
    ActivityLogType.TICKET_LINKED_FOR_ENTITY,
    ActivityLogType.TICKETS_CREATED_FOR_ENTITIES,
  ].includes(log.log_type);

  return (
    <Box display="flex" height="36px" alignItems="center">
      <Box
        width="24px"
        height="100%"
        padding="10px 4px 0 4px"
        position="relative"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="16px"
          height="16px"
        >
          {showUserAvatar ? (
            <LetterAvatar
              name={log.user_display_name}
              sx={{
                width: "14px",
                height: "14px",
                minWidth: null,
                minHeight: null,
              }}
            />
          ) : showTicketingIntegrationsIcon ? (
            <IntegrationImage
              name={log.ticketing_tool}
              width="14px"
              newLogo={true}
            />
          ) : (
            <SvgToIcon
              IconLogo={logIconsMapping[log.log_type]}
              width={16}
              height={16}
              style={{ color: colors.action_active }}
            />
          )}
        </Box>
        {includeBottomVerticalLine && <VerticalBottomLine colors={colors} />}
      </Box>
      <Box padding="8px">
        <CustomLogTypography>
          {log.user_id !== BREEZE_LOG_USER_ID && (
            <CustomLogTypography
              component="span"
              variant="body2"
              sx={{ fontWeight: 500 }}
            >
              {userDisplayName}{" "}
            </CustomLogTypography>
          )}
          {logContent}
        </CustomLogTypography>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="caption" color={colors.text_secondary}>
          •
        </Typography>
        <CustomTooltip
          title={getPrettyDateTime(
            log.log_timestamp.timestamp_utc,
            showTimeAtLocalTime
          )}
          placement="top"
          arrow
          disableInteractive
        >
          <Typography variant="caption" color={colors.text_secondary}>
            {timePassedSince(log.log_timestamp.timestamp_utc)}
          </Typography>
        </CustomTooltip>
      </Box>
    </Box>
  );
}
